import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import {
  useCreateProgrammeInvitation,
  useManagedProgrammes,
} from "model/useProgrammeInvitationQueries";
import { useEffect, useState } from "react";

interface Props {
  open: boolean;
  onClose: () => void;
  defaultProgrammeGuid?: string;
}

export function InviteFarmerDialog({
  open,
  onClose,
  defaultProgrammeGuid,
}: Props) {
  const { data: programmes, isLoading: programmesLoading } =
    useManagedProgrammes(open);
  const [programmeGuid, setProgrammeGuid] = useState<string>(
    defaultProgrammeGuid ?? ""
  );
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [tradingName, setTradingName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const createInvite = useCreateProgrammeInvitation(programmeGuid);

  useEffect(() => {
    if (open) {
      setEmail("");
      setName("");
      setTradingName("");
      setError(null);
      if (!programmeGuid && programmes && programmes.length === 1) {
        setProgrammeGuid(programmes[0].guid);
      }
    }
  }, [open, programmes, programmeGuid]);

  useEffect(() => {
    if (defaultProgrammeGuid) setProgrammeGuid(defaultProgrammeGuid);
  }, [defaultProgrammeGuid]);

  const handleSubmit = async () => {
    setError(null);
    const trimmed = email.trim();
    if (!trimmed || !/^\S+@\S+\.\S+$/.test(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!programmeGuid) {
      setError("Please choose a programme.");
      return;
    }
    try {
      await createInvite.mutateAsync({
        email: trimmed,
        name: name.trim() || undefined,
        tradingName: tradingName.trim() || undefined,
      });
      onClose();
    } catch (e) {
      setError((e as Error).message || "Could not send invitation.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Invite farmer to programme</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {programmesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel id="invite-programme-label">Programme</InputLabel>
              <Select
                labelId="invite-programme-label"
                label="Programme"
                value={programmeGuid}
                onChange={(e) => setProgrammeGuid(String(e.target.value))}
              >
                {(programmes ?? []).map((p) => (
                  <MenuItem key={p.guid} value={p.guid}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            label="Farmer email"
            type="email"
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="farmer@example.com"
            fullWidth
            autoFocus
          />
          <TextField
            label="Farmer name (optional)"
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Farmer"
            fullWidth
          />
          <TextField
            label="Trading name (optional)"
            size="small"
            value={tradingName}
            onChange={(e) => setTradingName(e.target.value)}
            placeholder="Hill Farm Ltd"
            fullWidth
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={createInvite.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={createInvite.isPending || !email || !programmeGuid}
        >
          {createInvite.isPending ? "Sending..." : "Send invitation"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
